<template>
  <div>
    <h2 v-if="content?.title" class="how-to-play__heading">
      {{ content.title }}
    </h2>
    <h2 v-else class="how-to-play__heading">
      {{ $t('how-to-play.heading') }}
    </h2>
    <div class="how-to-play">
      <div class="sticky-indicator"></div>
      <div v-if="content?.content" class="how-to-play__body">
        <StrapiBlocks :content="content.content" />
      </div>

      <div class="how-to-play__boxes">
        <div v-for="game in games" :key="game.name" class="box box--sm">
          <div>
            {{ game.name }}
          </div>
          <div class="font-bold text-base text-highlighted">
            {{ game.howToPlayInfo }}
          </div>
        </div>
      </div>
    </div>
    <DefaultFaqs />
  </div>
</template>
<script setup lang="ts">
import { StrapiBlocks } from 'vue-strapi-blocks-renderer';
import { computed, watch, nextTick, onMounted } from 'vue';

const config = useRuntimeConfig()
const logger = useFrontendLogger()
const contentStore = useContentStore()
const gamesStore = useGamesStore()

const games = computed(() => {
  return gamesStore.games?.filter((game) => {
    return game.isEnabled && game.name && game.howToPlayInfo
  })
})

const content = computed(() => {
  return contentStore.getContentBySlug(config.public.howToPlaySlug)
})

/**
 * Funkce, která po vykreslení obsahu najde externí odkazy
 * v kontejneru '.how-to-play__body' a přidá jim target="_blank".
 */
const addTargetBlankToExternalLinks = () => {
  // nextTick zajistí, že DOM (včetně obsahu z StrapiBlocks) byl aktualizován
  nextTick(() => {
    // Najdi kontejner, kde je dynamický obsah
    const contentContainer = document.querySelector('.how-to-play__body');

    if (contentContainer) {
      // Najdi všechny odkazy uvnitř kontejneru
      const links = contentContainer.querySelectorAll('a');

      links.forEach(link => {
        const href = link.getAttribute('href');

        // Získáme aktuální hostname pro porovnání s interními odkazy
        const currentHost = window.location.host;

        // Podmínky pro externí odkaz:
        // 1. Odkaz musí existovat
        // 2. Odkaz musí začínat na 'http', 'https', nebo '//' (schéma)
        // 3. Host odkazovaného URL nesmí být stejný jako aktuální host (ignorujeme interní odkazy)
        if (href) {
          try {
            const url = new URL(href, window.location.origin);

            if (url.host !== currentHost) {
              // Přidáme target="_blank"
              link.setAttribute('target', '_blank');
              // Přidáme rel="noopener noreferrer" pro zvýšenou bezpečnost
              link.setAttribute('rel', 'noopener noreferrer');
            }
          } catch (e) {
            // Při chybě (např. nevalidní URL), zkusíme jednoduchou kontrolu na externí URL
            if ((href.startsWith('http') || href.startsWith('//')) && !href.includes(currentHost)) {
              link.setAttribute('target', '_blank');
              link.setAttribute('rel', 'noopener noreferrer');
            }
          }
        }
      });
    }
  });
};

// Sledujeme změnu obsahu. Jakmile se data z Strapi načtou (asynchronní fetch),
// obsah se změní a my můžeme manipulovat s DOM.
watch(content, (newContent) => {
  if (newContent?.content) {
    addTargetBlankToExternalLinks();
  }
}, { immediate: true }); // Spustíme i při prvním načtení komponenty, pokud je obsah dostupný

// Fallback, i když by watch měl pokrýt asynchronní data
onMounted(() => {
  addTargetBlankToExternalLinks();
});

// fetch data
try {
  await gamesStore.fetchGames()
  await contentStore.fetchContents()
} catch (e: any) {
  logger.error(e)
}
</script>
